import { z } from "zod";

/** Resource types CloudServer manages (SPEC §5.3); `image` is a snapshot or a backup. */
export const ResourceKind = z.enum([
  "server",
  "ssh_key",
  "public_ip",
  "network",
  "firewall",
  "image",
  "volume",
  "floating_ip",
  "placement_group",
  "load_balancer",
  "certificate",
]);
export type ResourceKind = z.infer<typeof ResourceKind>;

/** A resource as operations and activities refer to it; `id` is null once it is gone. */
export const ResourceRef = z.object({
  kind: ResourceKind,
  id: z.uuid().nullable(),
  name: z.string(),
});
export type ResourceRef = z.infer<typeof ResourceRef>;

export const Actor = z.object({ id: z.uuid(), name: z.string() });
export type Actor = z.infer<typeof Actor>;

export const OperationStatus = z.enum(["running", "success", "error"]);
export type OperationStatus = z.infer<typeof OperationStatus>;

/**
 * One change CloudServer asked the provider for and follows to the end
 * (SPEC §5.4): creating a server, powering it off, attaching a network…
 */
export const Operation = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  /** What was done, e.g. `ssh_key.create`, `server.power_off`. */
  kind: z.string(),
  status: OperationStatus,
  /** 0–100. */
  progress: z.number().int().min(0).max(100),
  resources: z.array(ResourceRef),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  requestedBy: Actor.nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
});
export type Operation = z.infer<typeof Operation>;

/** An entry of a project's or a resource's activity feed. */
export const Activity = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  at: z.iso.datetime(),
  /** Null when CloudServer itself did it (sync, cleanup). */
  actor: Actor.nullable(),
  kind: z.string(),
  resource: ResourceRef,
  status: OperationStatus,
  operationId: z.uuid().nullable(),
});
export type Activity = z.infer<typeof Activity>;
